import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DOMPurify from 'dompurify';
import translate from '../../commonComponents/translate';
import FormWizardValidation from '../../services/formWizardValidation';
import FormInput from '../../commonComponents/forms/FormInput';
import AccessSessionProp from '../../commonComponents/propTypes/accessSessionProp';
import TeamProp from '../../commonComponents/propTypes/teamProp';
import courseProp from '../academy/propTypes/courseProp';
import journeyProp from '../journey/propTypes/journeyProp';
import Form from '../../commonComponents/forms/Form';
import Request from '../../services/request';
import Notifications from '../../services/notifications';
import CountryProp from '../../commonComponents/forms/Address/CountryProp';
import surveyProp from '../survey/propTypes/surveyProp';
import CategoryProp from '../academy/propTypes/categoryProp';
import TabInformation from './components/TabInfo';
import TabSessionDetails from './components/TabSessionDetails';
import TabTarget from './components/TabTarget';
import TabTimePeriod from './components/TabTimePeriod';
import TabRewards from './components/TabRewards';
import TabClose from './components/TabClose';
import userProp from '../../commonComponents/propTypes/userProp';
import i18n from '../../services/i18n';
import ImageInput from '../../commonComponents/forms/ImageInput';
import {
  checkPromoDate,
  handleImageUpload,
  PROMO_ERRORS,
  SESSION_MODE,
} from '../../services/utilityFunctions';
import HardSkillProp from '../challenges/propTypes/hardSkillProp';
import LoggedUserContext from '../../contextProviders/LoggedUserContext';
import CurrencyProp from '../profile/propTypes/currencyProp';
import CustomHTMLEditor from '../../commonComponents/forms/CustomHTMLEditor';
import { getTimeZoneAbbreviation } from '../../services/dateUtilityFunctions';
import ConfigsContext from '../../contextProviders/ConfigsContext';

function SessionCreateEdit(props) {
  const {
    t,
    _csrf,
    isEdit,
    isDuplicate,
    isFinished,
    isClosed,
    accessSession,
    teams,
    courses,
    journeys,
    languages,
    surveys,
    countries,
    categories,
    mapsKey,
    hardSkills,
    currencies,
    orgServices,
    organizationVatRates,
  } = props;
  const isWebexActive =
    orgServices?.webex?.enabled && orgServices?.webex?.config?.configured;

  const { tenant } = useContext(ConfigsContext);
  const isPaymentProviderNone =
    tenant?.settings?.paymentProviderType === 'none';

  const validateForm = (wizardObj, validator) => {
    const step = wizardObj.currentStep;
    let independentErrors = {};
    if (!$('#validator-sessionImage').val()) {
      independentErrors = {
        ...independentErrors,
        'validator-sessionImage': t('access_session.detail.missingImage'),
      };
    }

    if (step === 1) {
      if (!$('#validator-sessionImage').val()) {
        independentErrors = {
          ...independentErrors,
          'validator-sessionImage': t('access_session.detail.missingImage'),
        };
      }

      const promoDateRules = checkPromoDate(isEdit, accessSession);

      switch (promoDateRules) {
        case PROMO_ERRORS.CURRENT_TIME_GREATER:
          independentErrors = {
            ...independentErrors,
            promotionPriceDateEnd: t(
              'product.variation.promoDate.validation.1',
            ),
          };
          break;
        case PROMO_ERRORS.START_DATE_GREATER:
          independentErrors = {
            ...independentErrors,
            promotionPriceDateEnd: t('product.variation.promoDate.validation'),
          };
          break;
        default:
          break;
      }
    }

    if (step === 2) {
      $('input[name=typeSession]').each(function () {
        $(this).rules('add', {
          required: true,
        });
        if (!$(this).val()) {
          independentErrors = {
            ...independentErrors,
            typeSession: t('access_session.detail.inputMissing'),
          };
        }
      });
      $('input[id^=linkWebinar]').each(function () {
        $(this).rules('add', {
          required: () =>
            $('input[name=typeSession]:checked').val() === 'webinar',
        });
        if (!$(this).val()) {
          independentErrors = {
            ...independentErrors,
            linkWebinar: t('access_session.detail.inputMissing'),
          };
        }
      });
      $('input[id^=addressInput]').each(function () {
        $(this).rules('add', {
          required: () =>
            $('input[name=typeSession]:checked').val() === 'classroom',
        });
        if (!$(this).val()) {
          independentErrors = {
            ...independentErrors,
            address: t('access_session.detail.inputMissing'),
          };
        }
      });
    } else {
      $('input[name=typeSession]').each(function () {
        $(this).rules('remove');
      });
      $('input[id^=linkWebinar]').each(function () {
        $(this).rules('remove');
      });
      $('input[id^=addressInput]').each(function () {
        $(this).rules('remove');
      });
    }

    if (step === 3) {
      $('input[id^=minUsers]').each(function () {
        $(this).rules('add', {
          required: true,
          min: 0,
        });
        if (!$(this).val()) {
          independentErrors = {
            ...independentErrors,
            minUsers: t('access_session.detail.inputMissing'),
          };
        }
      });
      $('input[id^=maxUsers]').each(function () {
        $(this).rules('add', {
          required: true,
          min: () => Number.parseInt($('#minUsers').val(), 10),
        });
        if (!$(this).val()) {
          independentErrors = {
            ...independentErrors,
            maxUsers: t('access_session.detail.inputMissing'),
          };
        }
      });
    } else {
      $('input[id^=maxUsers]').each(function () {
        $(this).rules('remove');
      });
      $('input[id^=minUsers]').each(function () {
        $(this).rules('remove');
      });
    }

    if (step === 4) {
      $('input[id^=startDate]').each(function () {
        const startDateInMS = moment(
          $('#startDate').val(),
          'DD MMM YYYY - HH:mm',
        ).valueOf();
        const endDateInMS = moment(
          $('#endDate').val(),
          'DD MMM YYYY - HH:mm',
        ).valueOf();
        const timeIn5min = moment(new Date().valueOf())
          .add(4, 'minutes')
          .valueOf();
        const isWebex = $('#provider').val() === 'webex';

        // These are the required fields for webex, so only show the error message if some fields were updated
        const isEqualToInitialStartDate =
          isEdit && startDateInMS === accessSession?.startDate;
        const isEqualToInitialEndDate =
          isEdit && endDateInMS === accessSession?.endDate;
        const isEqualToInitialName =
          isEdit && $('#name').val() === accessSession?.name;

        let checkDate =
          isWebex &&
          (!isEqualToInitialStartDate ||
            !isEqualToInitialEndDate ||
            !isEqualToInitialName) &&
          startDateInMS <= timeIn5min;
        if (isDuplicate) {
          checkDate = isWebex && startDateInMS <= timeIn5min;
        }
        if (checkDate) {
          $(this).rules('add', {
            required: true,
            date_gt_custom: {
              date: startDateInMS,
              message: t('session.webinar.webex.startDate'),
              futureDate: timeIn5min,
              inputIdError: '#startDate',
            },
          });
        } else {
          $(this).rules('add', {
            required: true,
          });
        }

        if (!$(this).val()) {
          independentErrors = {
            ...independentErrors,
            startDate: t('access_session.detail.inputMissing'),
          };
        }
      });

      $('input[id^=endDate]').each(function () {
        const startDateInMS = moment(
          $('#startDate').val(),
          'DD MMM YYYY - HH:mm',
        ).valueOf();
        const endDateInMS = moment(
          $('#endDate').val(),
          'DD MMM YYYY - HH:mm',
        ).valueOf();
        const timeIn5min = moment(new Date().valueOf())
          .add(9, 'minutes')
          .valueOf();
        const moreThanOneDay = moment(startDateInMS).add(1, 'day').valueOf();
        const isWebex = $('#provider').val() === 'webex';

        // These are the required fields for webex, so only show the error message if some fields were updated
        const isEqualToInitialStartDate =
          isEdit && startDateInMS === accessSession?.startDate;
        const isEqualToInitialEndDate =
          isEdit && endDateInMS === accessSession?.endDate;
        const isEqualToInitialName =
          isEdit && $('#name').val() === accessSession?.name;

        let checkDate =
          isWebex &&
          (!isEqualToInitialStartDate ||
            !isEqualToInitialEndDate ||
            !isEqualToInitialName) &&
          endDateInMS <= timeIn5min;
        let checkDateMoreThenADay =
          isWebex &&
          (!isEqualToInitialStartDate ||
            !isEqualToInitialEndDate ||
            !isEqualToInitialName) &&
          endDateInMS >= moreThanOneDay;
        if (isDuplicate) {
          checkDate = isWebex && endDateInMS <= timeIn5min;
          checkDateMoreThenADay = isWebex && endDateInMS >= moreThanOneDay;
        }

        if (checkDate) {
          $(this).rules('add', {
            required: true,
            date_gt_custom: {
              date: endDateInMS,
              message: t('session.webinar.webex.endDate'),
              futureDate: timeIn5min,
              inputIdError: '#endDate',
            },
          });
        } else if (checkDateMoreThenADay) {
          $(this).rules('add', {
            required: true,
            date_gt_custom_1: {
              futureDate: moreThanOneDay,
              inputIdError: '#endDate',
            },
          });
        } else {
          $(this).rules('add', {
            required: true,
            date_gt: '#startDate',
          });
        }
        if (!$(this).val()) {
          independentErrors = {
            ...independentErrors,
            endDate: t('access_session.detail.inputMissing'),
          };
        }
      });

      $('input[id^=confirmationLinkTime]').each(function () {
        $(this).rules('add', {
          required: true,
          date_gt: '#startDate',
        });

        if (!$(this).val()) {
          independentErrors = {
            ...independentErrors,
            confirmationLinkTime: t('access_session.detail.inputMissing'),
          };
        }
      });
    } else {
      $('input[id^=startDate]').each(function () {
        $(this).rules('remove');
      });
      $('input[id^=endDate]').each(function () {
        $(this).rules('remove');
      });
      $('input[id^=confirmationLinkTime]').each(function () {
        $(this).rules('remove');
      });
    }

    if (Object.keys(independentErrors).length > 0) {
      validator.showErrors(independentErrors);
      wizardObj.stop();
      return false;
    }
    return true;
  };

  useEffect(() => {
    let lastStep = 1;
    FormWizardValidation.config();
    const { validator } = FormWizardValidation.init(
      '#addAccessSessionWizard',
      '#addAccessSessionForm',
      {
        rules: {
          title: { required: true },
          mode: { required: true },
          status: { required: true },
          'validator-sessionImage': {
            required: true,
          },
          category: { required: true },
          survey: {
            required: () => $('input[name=promoteSurvey]').is(':checked'),
          },
        },
        messages: {
          title: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          status: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          sessionImage: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          category: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          typeSession: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          linkWebinar: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          hostClassroom: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          hostWebinar: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          trainerClassroom: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          trainerWebinar: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
          startDate: {
            required: t('ERROR_FIELD_REQUIRED'),
            date_gt_custom: t('session.webinar.webex.startDate'),
          },
          endDate: {
            required: t('ERROR_FIELD_REQUIRED'),
            date_gt: t('access_session.detail.endDate.required.greater'),
            date_gt_custom: t('session.webinar.webex.endDate'),
            date_gt_custom_1: t('session.webinar.webex.endDate.oneDay'),
          },
          confirmationLinkTime: {
            required: t('ERROR_FIELD_REQUIRED'),
            date_gt: t(
              'access_session.detail.confirmationLinkTime.required.greater',
            ),
          },
          survey: {
            required: t('ERROR_FIELD_REQUIRED'),
          },
        },
        errorPlacement: (error, element) => {
          if (element.attr('data-img')) {
            error.insertAfter($(`#image-${element.attr('data-img')}`));
          } else {
            error.insertAfter(element);
          }
        },
        submitHandler: form => {
          console.log(form);
        },
      },
      wizardObj => {
        window.scrollTo(0, 0);
        const sessionType = $('#isOnline').val();
        const currentStep = wizardObj.getStep();
        if (sessionType === 'online') {
          switch (lastStep) {
            case 4:
              if (currentStep === 5) {
                wizardObj.goTo(6);
              }
              break;
            case 6:
              if (currentStep === 5) {
                wizardObj.goTo(4);
              }
              break;
            default:
              break;
          }
        }
        lastStep = wizardObj.getStep();
      },
      wizardObj => {
        if (!validateForm(wizardObj, validator)) {
          wizardObj.stop();
        }
        if (validator.form() !== true) {
          wizardObj.stop();
        }
      },
    );
  }, []);

  const { loggedUser } = useContext(LoggedUserContext);
  const standardRate =
    organizationVatRates?.vatRates?.find(r => r?.type === 'standard')?.type ||
    null;
  const [state, setState] = useState({
    ...accessSession,
    mode: accessSession?.mode || 'private',
    emails: accessSession?.emails || [],
    currency: accessSession?.currency
      ? accessSession.currency.toUpperCase()
      : loggedUser?.currency?.toUpperCase() || 'EUR',
    availableCountries: accessSession?.availableCountries || [],
    vatRate: !isPaymentProviderNone
      ? accessSession?.vatRate || standardRate
      : null,
    vatRateCategory: !isPaymentProviderNone
      ? accessSession?.vatRateCategory || 'other'
      : null,
    promoteSurvey: accessSession?.survey,
  });

  useEffect(() => {
    const newStatus =
      state.status === 'unstarted' ||
      state.status === 'ongoing' ||
      state.status === 'finished'
        ? 'planned'
        : state.status;
    setState({
      ...state,
      status: newStatus,
    });
  }, []);

  const validationRules = {};

  const handleChange = field => value => {
    let newValue = value;
    switch (field) {
      case 'coins':
        newValue = value ? Number.parseInt(value, 10) : null;
        break;
      case 'price':
      case 'promotionsPrice':
        newValue =
          value && parseInt(value, 10) >= 0
            ? Number.parseInt(value * 100, 10)
            : null;
        break;
      case 'targetTeams':
        newValue = Array.isArray(value) ? value : [value];
        break;
      case 'contentAvailableBeforePayment':
        newValue =
          typeof value?.target?.checked !== 'undefined'
            ? value.target.checked
            : value;
        break;
      case 'vatRateCategory':
        if (value !== 'other') {
          const correctVatRate = organizationVatRates?.vatRates?.find(
            r =>
              r.percentage === organizationVatRates?.vatRatesCategories[value],
          );
          setState(prevState => ({
            ...prevState,
            vatRate: correctVatRate?.type,
            [field]: value,
          }));
        } else {
          setState(prevState => ({
            ...prevState,
            [field]: value,
            vatRate: standardRate,
          }));
        }
        return;
      default:
        newValue = value;
        break;
    }

    if (field === 'promoteSurvey' && value === false) {
      setState(prevState => ({ ...prevState, survey: null }));
    }

    setState(prevState => ({ ...prevState, [field]: newValue }));
  };

  const handleAddOption = field => value =>
    setState(prevState => ({
      ...prevState,
      [field]: prevState[field].concat(value),
    }));

  const handleClassroom = (field, value) => {
    setState(prevState => {
      const classroom = prevState.classroom || {};
      if (field === 'placeLocation') {
        classroom[field] = typeof value === 'string' ? value : value.address_1;
      } else {
        classroom[field] = value;
      }
      return { ...prevState, classroom };
    });
  };

  const handleAddTagOption = field => value =>
    setState(prevState => ({
      ...prevState,
      [field]: value,
    }));
  const handleRemoveOption = field => value =>
    setState(prevState => ({
      ...prevState,
      [field]: prevState[field].filter(v => v !== value),
    }));

  const handleErrorMessage = err => {
    const errorMessage = err?.responseJSON?.error.split('’');
    if (errorMessage && errorMessage[1] === ' does not have host privilege') {
      const webexUserError = errorMessage[0].split('‘')[1];
      Notifications.showNotificationError(
        t('error'),
        i18n.__('webex.error.user', webexUserError),
      );
    } else {
      Notifications.showErrorFromRequest(err, '500error-title');
    }
  };

  const handleSubmit = async () => {
    let params = {
      ...state,
      _id: undefined,
      owner: loggedUser._id,
      courses:
        $('input[name=typeSession]:checked').val() === 'online' ||
        $('input[name=typeSession]:checked').val() === 'classroom'
          ? state.courses
          : [],
      journeys:
        $('input[name=typeSession]:checked').val() === 'online'
          ? state.journeys
          : [],
      classroom:
        $('input[name=typeSession]:checked').val() === 'classroom'
          ? state.classroom
          : {},
      webinar:
        $('input[name=typeSession]:checked').val() === 'webinar'
          ? state.webinar
          : {},
      mode: state.mode || SESSION_MODE.PRIVATE,
      currency: state.currency.toLowerCase(),
      dateTimeZone: getTimeZoneAbbreviation(),
      description: DOMPurify.sanitize(state.description),
    };

    if (params.type === 'online') {
      params = {
        ...params,
        host: null,
      };
    } else if (params.type === 'webinar' && !params.webinar.provider) {
      params = {
        ...params,
        webinar: {
          ...params.webinar,
          provider: 'generic',
        },
      };
    }
    // Make sure that we don't sent max and min users if session is private
    // Also if is public, targetTeams, users, emails should be empty
    if (state.mode === 'private') {
      params = {
        ...params,
        maxUsers: null,
        minUsers: null,
      };
    } else {
      params = {
        ...params,
        targetTeams: [],
        targetUsers: [],
        emails: [],
      };
    }
    if (isEdit && !isDuplicate) {
      new Request(_csrf)
        .put(`/api/access-session/${state._id}`, params, false, true)
        .done(() => {
          $('.loader-button').attr('disabled', true);
          Notifications.showNotificationSuccess(
            t('success'),
            t('success_update'),
          );
          window.location.href = '/academy/my-sessions';
        })
        .fail(err => {
          handleErrorMessage(err);
        });
    } else {
      new Request(_csrf)
        .post('/api/access-session', params, false, true)
        .done(() => {
          $('.loader-button').attr('disabled', true);
          Notifications.showNotificationSuccess(
            t('success'),
            t('success_create'),
          );
          window.location.href = '/academy/my-sessions';
        })
        .fail(err => {
          handleErrorMessage(err);
        });
    }
  };

  const handleImageChange =
    (id, onChange = null) =>
    async img => {
      try {
        const data = await handleImageUpload(t, _csrf, img, false);
        const changeMethod =
          onChange || handleChange(id === 'sessionImage' ? 'image' : id);
        changeMethod(data);
        $(`validator-${id}`).val();
      } catch (error) {
        Notifications.showErrorFromRequest(error, 'error_imageUpload');
      }
    };

  const onChangeUser = field => user => {
    setState(prev => {
      const prevArray = prev[field] || [];
      return {
        ...prev,
        [field]: [...new Set([...prevArray, user])],
      };
    });
  };
  const onUnselectUser = field => user => {
    setState(prev => {
      const prevArray = prev[field] || [];
      return {
        ...prev,
        [field]: prevArray.filter(el => el !== user),
      };
    });
  };

  const handleCustomHtmlChange = (id, value) => {
    setState(prevState => ({
      ...prevState,
      [id]: value,
    }));
  };

  const createCustomHTMLEditor = (
    field,
    typeParam,
    required,
    labelClass,
    containerClass,
  ) => (
    <CustomHTMLEditor
      id={field}
      name={field}
      value={state[field] !== null ? state[field] : ''}
      onChange={(id, value) => handleCustomHtmlChange(id, value)}
      label={t(`access_session.detail.${field}`)}
      placeholder={t(`access_session.detail.${field}.placeholder`)}
      labelClassName={labelClass}
      containerClassName={containerClass}
      groupProps={{ className: 'row' }}
      inputProps={{
        required,
      }}
      disabled={isFinished || isClosed}
    />
  );

  const createInput = (
    field,
    typeParam,
    required,
    labelClass,
    containerClass,
    inputValue,
    onChange,
    hasHint,
  ) => {
    const inputType = typeParam || 'text';
    let value = typeof inputValue !== 'undefined' ? inputValue : state[field];
    const changeMethod =
      typeof onChange !== 'undefined' ? onChange : handleChange(field);

    if (field === 'price' || field === 'promotionsPrice') {
      value = state?.[field] ? state[field] / 100 : null;
    }

    let hintText;
    if (hasHint) {
      hintText =
        field === 'price' || field === 'promotionsPrice'
          ? t('vatExcluded')
          : t(`access_session.detail.${field}.hint`);
    }
    return (
      <FormInput
        id={field}
        name={field}
        value={value !== null ? value : ''}
        onChange={changeMethod}
        label={t(`access_session.detail.${field}`)}
        placeholder={t(`access_session.detail.${field}.placeholder`)}
        labelClassName={labelClass}
        containerClassName={containerClass}
        type={inputType}
        hint={hintText}
        showClassName={typeParam !== 'checkbox'}
        currency={currencies.find(c => c.code === state?.currency).symbol}
        groupProps={{ className: 'row' }}
        inputProps={{
          required,
        }}
        disabled={isFinished || isClosed}
      />
    );
  };
  const createPromoDateTime = (id, classWrapper) => (
    <FormInput
      id={id}
      name={id}
      value={state[id] || ''}
      onChange={newValue => handleChange(id)(newValue)}
      label={t(`product.variation.${id}`)}
      placeholder={t(`product.variation.${id}`)}
      type="dateTime"
      labelClassName="col-12 text-left"
      containerClassName="col-12"
      groupProps={{ className: `${classWrapper} col-lg-6 col-12` }}
      disabled={isFinished || isClosed}
      inputProps={{
        allowClear: true,
      }}
    />
  );
  const createInputHint = (
    field,
    typeParam,
    required,
    labelCol,
    containerCol,
    inputValue,
    onChange,
  ) => {
    const inputType = typeParam || 'text';
    const value = typeof inputValue !== 'undefined' ? inputValue : state[field];
    const changeMethod =
      typeof onChange !== 'undefined' ? onChange : handleChange(field);

    return (
      <FormInput
        id={field}
        name={field}
        value={value !== null ? value : ''}
        onChange={changeMethod}
        label={t(`access_session.detail.${field}`)}
        placeholder={t(`access_session.detail.${field}.placeholder`)}
        labelClassName={`${labelCol} text-right`}
        containerClassName={containerCol}
        hint={t(`access_session.detail.${field}.hint`)}
        type={inputType}
        groupProps={{ className: 'row' }}
        inputProps={{
          required,
        }}
        disabled={isFinished || isClosed}
      />
    );
  };
  const createSelectAvailableCountries = (
    field,
    items,
    required,
    labelClass,
    containerClass,
  ) => (
    <FormInput
      id={field}
      name={field}
      value={state[field] !== null ? state[field] : ''}
      onChange={handleAddOption(field)}
      label={t('academy.session.availableCountries')}
      labelClassName={labelClass}
      containerClassName={containerClass}
      type="select"
      placeholder={t('academy.session.availableCountries.placeholder')}
      hint={t('academy.session.availableCountries.hint')}
      groupProps={{ className: 'row' }}
      disabled={isFinished || isClosed}
      inputProps={{
        onUnselect: handleRemoveOption(field),
        width: '100%',
        multiple: true,
        items,
      }}
    />
  );
  const selectMultiple = (field, items, labelClass, containerClass) => (
    <FormInput
      id={field}
      name={field}
      value={state[field] || []}
      onChange={handleAddOption(field)}
      label={t(`access_session.detail.${field}`)}
      labelClassName={labelClass}
      containerClassName={containerClass}
      type="select2"
      placeholder={t(`access_session.detail.${field}.placeholder`)}
      hint={
        field === 'courses' || field === 'journeys'
          ? t(`access_session.detail.${field}.hint`)
          : undefined
      }
      groupProps={{ className: 'row' }}
      inputProps={{
        onUnselect: handleRemoveOption(field),
        width: '100%',
        multiple: true,
        items,
        allowClear: true,
      }}
      disabled={isFinished || isClosed}
    />
  );
  const selectTags = field => {
    const value = state?.[field] || [];
    const changeMethod = handleAddTagOption(field);
    return (
      <FormInput
        hint={t('access_session.detail.emails.hint')}
        id="emails"
        name="emails"
        value={value !== null ? value : []}
        onChange={changeMethod}
        label={t('access_session.detail.emails')}
        placeholder={t('access_session.detail.emails.placeholder')}
        labelClassName="col-3 text-right"
        containerClassName="col-9"
        type="tags"
        groupProps={{ className: 'row' }}
        inputProps={{
          validationRegex:
            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
        }}
        disabled={isFinished || isClosed}
      />
    );
  };
  const valueStatus = field => {
    let value = '';
    if (state[field] !== null) {
      if (state[field] === 'finished' || state[field] === 'ongoing') {
        value = 'planned';
      } else {
        value = state[field];
      }
    }
    return value;
  };
  const select = (
    field,
    items,
    labelClass,
    containerClass,
    inputPreferences = {},
    hasLabel = true,
    hint,
    disabled = false,
  ) => {
    const shouldDisable = disabled || (isFinished && field !== 'status');
    return (
      <FormInput
        id={field}
        name={field}
        value={valueStatus(field)}
        onChange={handleChange(field)}
        label={hasLabel && t(`access_session.detail.${field}`)}
        labelClassName={labelClass}
        containerClassName={containerClass}
        type="select"
        placeholder={t(`access_session.detail.${field}.placeholder`)}
        groupProps={{ className: 'row' }}
        inputProps={{
          width: '100%',
          multiple: false,
          items,
          allowClear: field !== 'currency',
          ...inputPreferences,
        }}
        disabled={shouldDisable || isClosed}
        hint={hint || ''}
      />
    );
  };
  const createUser = (field, labelClass, containerClass) => {
    const userTypeNames = {};
    Object.values(userProp.TYPES).forEach(userType => {
      userTypeNames[userType] = i18n.__(`user_type_${userType}`);
    });
    return (
      <FormInput
        type="users"
        id={field}
        name={field}
        labelClassName={labelClass}
        containerClassName={containerClass}
        label={t(`access_session.detail.${field}`)}
        placeholder={t(`access_session.detail.${field}.placeholder`)}
        groupProps={{ className: 'row' }}
        onChange={handleChange(field)}
        value={state[field] || ''}
        width="100%"
        className="form-control m-input"
        inputProps={{
          showFilter: true,
          multiple: false,
          userTypeNames,
          languages,
          hardSkills,
          allowClear: true,
        }}
        disabled={isFinished || isClosed}
      />
    );
  };
  const createTrainer = (field, labelClass, containerClass) => {
    const userTypeNames = {};
    Object.values(userProp.TYPES).forEach(userType => {
      userTypeNames[userType] = i18n.__(`user_type_${userType}`);
    });
    return (
      <FormInput
        type="users"
        id={field}
        name={field}
        labelClassName={labelClass}
        containerClassName={containerClass}
        label={t(`access_session.detail.${field}`)}
        placeholder={t(`access_session.detail.${field}.placeholder`)}
        groupProps={{ className: 'row' }}
        onChange={handleChange(field)}
        value={state[field] || ''}
        width="100%"
        className="form-control m-input"
        inputProps={{
          showFilter: true,
          multiple: false,
          userTypeNames,
          filterUrl: '/table/user/trainers/list',
          url: '/user/trainers/table/list',
          languages,
          hardSkills,
          allowClear: true,
        }}
        disabled={isFinished || isClosed}
      />
    );
  };
  const createUserMultiple = (field, labelClass, containerClass) => {
    const userTypeNames = {};
    Object.values(userProp.TYPES).forEach(userType => {
      userTypeNames[userType] = i18n.__(`user_type_${userType}`);
    });
    return (
      <FormInput
        type="users"
        id={field}
        name={field}
        labelClassName={labelClass}
        containerClassName={containerClass}
        label={t(`access_session.detail.${field}`)}
        placeholder={t(`access_session.detail.${field}.placeholder`)}
        groupProps={{ className: 'row' }}
        onChange={onChangeUser(field)}
        value={state[field] || []}
        width="100%"
        className="form-control m-input"
        inputProps={{
          showFilter: true,
          multiple: true,
          userTypeNames,
          onUnselect: onUnselectUser(field),
          languages,
          hardSkills,
          allowClear: true,
        }}
        disabled={isFinished || isClosed}
      />
    );
  };
  const createImage = (
    field,
    containerClass,
    labelCol,
    placeholder,
    cropRect,
    onChange = null,
    inputValue = null,
  ) => {
    const fieldName = field === 'sessionImage' ? 'image' : field;
    const value = inputValue || state[fieldName];
    const initialValue = value && value.url ? value.url : '';
    return (
      <>
        <ImageInput
          key={field}
          id={`image-${field}`}
          inputId={field}
          name={field}
          type="image"
          initialValue={initialValue}
          onChange={handleImageChange(field, onChange)}
          inputProps={{ ...cropRect }}
          label={t(`access_session.detail.${field}`)}
          containerClassName={containerClass}
          labelClassName={labelCol}
          groupClassName="form-group m-form__group row"
          disabled={isFinished || isClosed}
        />
        <input
          type="hidden"
          id={`validator-${field}`}
          name={`validator-${field}`}
          value={initialValue}
          data-img={`${field}`}
          disabled={isFinished || isClosed}
        />
      </>
    );
  };
  const title = isEdit ? t('edit_access_session') : t('add_access_session');

  const disabledSubmitBtn =
    !isClosed || (isFinished && state.status === 'closed');
  const isOnlineSession = state.type === 'online';

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="m-portlet m-portlet--mobile">
          <div className="m-portlet__head">
            <div className="m-portlet__head-caption">
              <div className="m-portlet__head-title">
                <h3 className="m-portlet__head-text">{title}</h3>
              </div>
            </div>
          </div>
          <div className="m-portlet__body">
            <div
              className="m-wizard m-wizard--1 m-wizard--primary"
              id="addAccessSessionWizard"
            >
              <div className="m-wizard__head m-portlet__padding-x">
                <div className="m-wizard__progress">
                  <div className="progress" style={{ width: '100%' }}>
                    <div
                      className="progress-bar"
                      role="progressbar"
                      aria-valuenow="100"
                      aria-valuemin="50"
                      aria-valuemax="100"
                    >
                      {}
                    </div>
                  </div>
                  <div className="m-wizard__nav">
                    <div className="m-wizard__steps">
                      <div
                        className="m-wizard__step m-wizard__step--current"
                        m-wizard-target="tab1"
                      >
                        <div className="m-wizard__step-info">
                          <a href="#tab1" className="m-wizard__step-number">
                            <span>
                              <span>1</span>
                            </span>
                          </a>
                          <div className="m-wizard__step-line">
                            <span />
                          </div>
                          <div className="m-wizard__step-label">
                            {t('access_session.detail.wizardStep1')}
                          </div>
                        </div>
                      </div>
                      <div className="m-wizard__step" m-wizard-target="tab2">
                        <div className="m-wizard__step-info">
                          <a href="#tab2" className="m-wizard__step-number">
                            <span>
                              <span>2</span>
                            </span>
                          </a>
                          <div className="m-wizard__step-line">
                            <span />
                          </div>
                          <div className="m-wizard__step-label">
                            {t('access_session.detail.wizardStep2')}
                          </div>
                        </div>
                      </div>
                      <div className="m-wizard__step" m-wizard-target="tab3">
                        <div className="m-wizard__step-info">
                          <a href="#tab3" className="m-wizard__step-number">
                            <span>
                              <span>3</span>
                            </span>
                          </a>
                          <div className="m-wizard__step-line">
                            <span />
                          </div>
                          <div className="m-wizard__step-label">
                            {t('access_session.detail.wizardStep3')}
                          </div>
                        </div>
                      </div>
                      <div className="m-wizard__step" m-wizard-target="tab4">
                        <div className="m-wizard__step-info">
                          <a href="#tab4" className="m-wizard__step-number">
                            <span>
                              <span>4</span>
                            </span>
                          </a>
                          <div className="m-wizard__step-line">
                            <span />
                          </div>
                          <div className="m-wizard__step-label">
                            {t('access_session.detail.wizardStep4')}
                          </div>
                        </div>
                      </div>
                      <div
                        className="m-wizard__step"
                        m-wizard-target="tab5"
                        style={{ width: isOnlineSession && 0 }}
                      >
                        <div className="m-wizard__step-info">
                          {!isOnlineSession && (
                            <>
                              <a href="#tab5" className="m-wizard__step-number">
                                <span>
                                  <span>{!isOnlineSession ? 5 : ''}</span>
                                </span>
                              </a>
                              <div className="m-wizard__step-line">
                                <span />
                              </div>
                              <div className="m-wizard__step-label">
                                {t('access_session.detail.wizardStep5')}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="m-wizard__step" m-wizard-target="tab6">
                        <div className="m-wizard__step-info">
                          <a href="#tab6" className="m-wizard__step-number">
                            <span>
                              <span>{!isOnlineSession ? '6' : '5'}</span>
                            </span>
                          </a>
                          <div className="m-wizard__step-line">
                            <span />
                          </div>
                          <div className="m-wizard__step-label">
                            {t('access_session.detail.wizardStep6')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-wizard m-wizard__form">
                <Form
                  className="form-horizontal"
                  id="addAccessSessionForm"
                  submitMethod={handleSubmit}
                  validationRules={validationRules}
                >
                  {submitHandler => (
                    <>
                      <input type="hidden" id="isOnline" value={state.type} />
                      <div
                        className="m-wizard__form-step m-wizard__step--current"
                        id="tab1"
                      >
                        <TabInformation
                          session={state}
                          status={accessSession.status}
                          createInput={createInput}
                          createCustomHTMLEditor={createCustomHTMLEditor}
                          select={select}
                          createUser={createUser}
                          createImage={createImage}
                          categories={categories}
                          languages={languages}
                          currencies={currencies}
                          countries={countries}
                          createSelectAvailableCountries={
                            createSelectAvailableCountries
                          }
                          isFinished={isFinished || isClosed}
                          isStatusFinished={isFinished}
                          createPromoDateTime={createPromoDateTime}
                          organizationVatRates={organizationVatRates}
                          isPaymentProviderNone={isPaymentProviderNone}
                        />
                      </div>
                      <div className="m-wizard__form-step" id="tab2">
                        <TabSessionDetails
                          _csrf={_csrf}
                          createUser={createUser}
                          createTrainer={createTrainer}
                          accessSession={state}
                          selectMultiple={selectMultiple}
                          handleChange={handleChange}
                          courses={courses}
                          journeys={journeys}
                          countries={countries}
                          mapsKey={mapsKey}
                          isFinished={isFinished || isClosed}
                          onClassroomUpdate={handleClassroom}
                          isWebexActive={isWebexActive}
                          isDuplicate={isDuplicate}
                        />
                      </div>
                      <div className="m-wizard__form-step" id="tab3">
                        <TabTarget
                          accessSession={state}
                          createUserMultiple={createUserMultiple}
                          createInput={createInput}
                          selectMultiple={selectMultiple}
                          teams={teams}
                          isFinished={isFinished}
                          selectTags={selectTags}
                          t={t}
                        />
                      </div>
                      <div className="m-wizard__form-step" id="tab4">
                        <TabTimePeriod createInput={createInput} t={t} />
                      </div>
                      <div className="m-wizard__form-step" id="tab5">
                        <TabRewards
                          createInput={createInput}
                          createImage={createImage}
                          createInputHint={createInputHint}
                          t={t}
                          handleChange={handleChange}
                          reward={state.reward}
                          isFinished={isFinished}
                        />
                      </div>
                      <div className="m-wizard__form-step" id="tab6">
                        <TabClose
                          accessSession={accessSession}
                          createInput={createInput}
                          select={select}
                          formState={state}
                          documents={state.documents}
                          createImage={createImage}
                          surveys={surveys}
                          handleChange={handleChange}
                          isFinished={isFinished}
                        />
                      </div>
                      <div className="m-portlet__foot">
                        <div className="m-form__actions">
                          <div className="row">
                            <div className="col-sm-3 col-md-3 col-lg-3">
                              <button
                                type="button"
                                className="btn default button-previous"
                                onClick={() => {
                                  window.location.href = '/academy/my-sessions';
                                }}
                              >
                                <i className="fa fa-angle-left" />
                                {` ${t('back_to_list')}`}
                              </button>
                            </div>
                            <div className="col-sm-6 col-md-4 col-lg-9">
                              <button
                                type="button"
                                data-wizard-action="prev"
                                className="btn default button-previous"
                              >
                                <i className="fa fa-angle-left" />
                                {` ${t('back')}`}
                              </button>
                              <button
                                type="button"
                                data-wizard-action="next"
                                className="btn btn-outline green-jungle button-next"
                              >
                                {`${t('continue')} `}
                                <i className="fa fa-angle-right" />
                              </button>
                              {disabledSubmitBtn && (
                                <button
                                  type="submit"
                                  data-wizard-action="submit"
                                  className="btn green-jungle button-submit loader-button"
                                  onClick={submitHandler}
                                >
                                  {`${t('submit')} `}
                                  <i className="fa fa-check" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

SessionCreateEdit.defaultProps = {
  accessSession: AccessSessionProp.defaultProp,
  isDuplicate: false,
  isFinished: false,
  isClosed: false,
  mapsKey: '',
  organizationVatRates: {
    vatRates: [],
    vatRatesCategories: {},
  },
};

SessionCreateEdit.propTypes = {
  t: PropTypes.func.isRequired,
  _csrf: PropTypes.string.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isDuplicate: PropTypes.bool,
  isFinished: PropTypes.bool,
  isClosed: PropTypes.bool,
  accessSession: PropTypes.shape(AccessSessionProp.propType),
  courses: PropTypes.arrayOf(PropTypes.shape(courseProp.propType)).isRequired,
  journeys: PropTypes.arrayOf(PropTypes.shape(journeyProp.propType)).isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape(TeamProp.propType)).isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  hardSkills: PropTypes.arrayOf(PropTypes.shape(HardSkillProp.propType))
    .isRequired,
  surveys: PropTypes.arrayOf(PropTypes.shape(surveyProp.propType)).isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  mapsKey: PropTypes.string,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  orgServices: PropTypes.shape({
    webex: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
      config: PropTypes.shape({
        configured: PropTypes.bool,
      }),
    }).isRequired,
  }).isRequired,
  organizationVatRates: PropTypes.shape({
    country: PropTypes.string,
    isDefault: PropTypes.bool,
    vatRates: PropTypes.arrayOf(
      PropTypes.shape({
        percentage: PropTypes.number,
        type: PropTypes.string,
      }),
    ),
    vatRatesCategories: PropTypes.shape({
      accommodation: PropTypes.number,
      'admission to cultural events': PropTypes.number,
      'admission to entertainment events': PropTypes.number,
      'admission to sporting events': PropTypes.number,
      advertising: PropTypes.number,
      'agricultural supplies': PropTypes.number,
      'baby foodstuffs': PropTypes.number,
      bikes: PropTypes.number,
      books: PropTypes.number,
      'childrens clothing': PropTypes.number,
      'domestic fuel': PropTypes.number,
      'domestic services': PropTypes.number,
      ebooks: PropTypes.number,
      electricity: PropTypes.number,
      foodstuffs: PropTypes.number,
      hotels: PropTypes.number,
      medical: PropTypes.number,
      newspapers: PropTypes.number,
      'passenger transport': PropTypes.number,
      pharmaceuticals: PropTypes.number,
      'property renovations': PropTypes.number,
      restaurants: PropTypes.number,
      'social housing': PropTypes.number,
      water: PropTypes.number,
      wine: PropTypes.number,
    }),
    _id: PropTypes.string,
  }),
};

export default translate(SessionCreateEdit);
